import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Copy, Mail, Users, Clock, CheckCircle, AlertCircle,
  BarChart3, Send, Download, Settings, Eye, Trash2, Plus,
  ChevronRight, Calendar, Shield, UserPlus, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AssessmentDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  
  // Pre-approved candidates
  const [newApprovedEmail, setNewApprovedEmail] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('company_token');
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchAssessment(token);
    fetchCandidates(token);
  }, [id, navigate]);

  const fetchAssessment = async (token) => {
    try {
      const res = await axios.get(`${API}/api/company/assessments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessment(res.data.assessment);
    } catch (err) {
      toast.error('Assessment not found');
      navigate('/company/assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async (token) => {
    try {
      const res = await axios.get(`${API}/api/company/assessments/${id}/candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(res.data.candidates || []);
    } catch (err) {
      console.log('Failed to fetch candidates');
    }
  };

  const handleInvite = async () => {
    const emails = inviteEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
    if (emails.length === 0) {
      toast.error('Please enter at least one email');
      return;
    }

    setInviting(true);
    try {
      const token = localStorage.getItem('company_token');
      const res = await axios.post(`${API}/api/company/assessments/${id}/invite`, {
        emails,
        custom_message: customMessage || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Invited ${res.data.invited} candidates`);
      setShowInviteModal(false);
      setInviteEmails('');
      setCustomMessage('');
      fetchCandidates(token);
      fetchAssessment(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setInviting(false);
    }
  };

  const copyLink = () => {
    // Use production domain for shared links - never use preview URLs
    // New format: Link without code, company shares link + access code separately
    const productionDomain = "https://evalentx.com";
    const link = `${productionDomain}/assessment/${id}`;
    navigator.clipboard.writeText(link);
    toast.success('Assessment link copied! Share this link along with the access code.');
  };

  // Add pre-approved candidate
  const handleAddApprovedCandidate = async (e) => {
    e.preventDefault();
    const email = newApprovedEmail.trim().toLowerCase();
    
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Check if already exists
    if (candidates.some(c => c.email.toLowerCase() === email)) {
      toast.error('This email is already in the pre-approved list');
      return;
    }
    
    setAddingEmail(true);
    try {
      const token = localStorage.getItem('company_token');
      await axios.post(`${API}/api/company/assessments/${id}/pre-approve`, {
        email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Candidate added to pre-approved list');
      setNewApprovedEmail('');
      fetchCandidates(token);
      fetchAssessment(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add candidate');
    } finally {
      setAddingEmail(false);
    }
  };

  // Remove pre-approved candidate
  const handleRemoveCandidate = async (candidateId, email) => {
    if (!window.confirm(`Remove ${email} from pre-approved list?`)) return;
    
    setRemovingEmail(candidateId);
    try {
      const token = localStorage.getItem('company_token');
      await axios.delete(`${API}/api/company/assessments/${id}/candidates/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Candidate removed');
      fetchCandidates(token);
      fetchAssessment(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove candidate');
    } finally {
      setRemovingEmail(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Completed</span>;
      case 'started':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">In Progress</span>;
      case 'registered':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">Registered</span>;
      case 'invited':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Invited</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!assessment) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/company/assessments')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{assessment.title}</h1>
                <p className="text-sm text-gray-500">{assessment.duration_minutes} mins • {assessment.total_points} points</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Link
                to={`/company/assessments/${id}/analytics`}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <BarChart3 size={18} />
                Analytics
              </Link>
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
                data-testid="invite-candidates-btn"
              >
                <Mail size={18} />
                Invite Candidates
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{assessment.candidates_invited || 0}</p>
                <p className="text-sm text-gray-500">Invited</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{assessment.candidates_started || 0}</p>
                <p className="text-sm text-gray-500">Started</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{assessment.candidates_completed || 0}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{assessment.duration_minutes}</p>
                <p className="text-sm text-gray-500">Minutes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Share Link */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-lg">Share Assessment Link</h3>
              <p className="text-purple-200 text-sm">Candidates can register using this link</p>
            </div>
            <div className="flex items-center gap-3">
              <code className="px-3 py-2 bg-white/20 rounded-lg text-sm font-mono">
                {assessment.access_code}
              </code>
              <button
                onClick={copyLink}
                className="flex items-center gap-2 px-4 py-2 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50"
              >
                <Copy size={16} />
                Copy Link
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'overview' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('preapproved')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'preapproved' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="preapproved-tab"
          >
            <UserPlus size={16} />
            Pre-Approved ({candidates.filter(c => c.status === 'invited').length})
          </button>
          <button
            onClick={() => setActiveTab('candidates')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'candidates' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Candidates ({candidates.length})
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* MCQs */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">MCQ Questions ({assessment.total_mcqs})</h3>
              </div>
              <div className="p-6">
                {assessment.mcqs?.length > 0 ? (
                  <div className="space-y-3">
                    {assessment.mcqs.map((mcq, idx) => (
                      <div key={mcq.id} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium text-gray-900">Q{idx + 1}: {mcq.question}</p>
                        <p className="text-sm text-gray-500 mt-1">{mcq.options.length} options • {mcq.points} points</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No MCQs added</p>
                )}
              </div>
            </div>

            {/* Coding Problems */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Coding Problems ({assessment.total_coding_problems})</h3>
              </div>
              <div className="p-6">
                {assessment.coding_problems?.length > 0 ? (
                  <div className="space-y-3">
                    {assessment.coding_problems.map((problem, idx) => (
                      <div key={problem.problem_id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{problem.title}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            problem.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                            problem.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {problem.difficulty?.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-purple-600">{problem.points} pts</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No coding problems added</p>
                )}
              </div>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-xl border border-gray-200 lg:col-span-2">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Assessment Settings</h3>
              </div>
              <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-medium text-gray-900">{assessment.duration_minutes} minutes</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Passing Score</p>
                  <p className="font-medium text-gray-900">{assessment.passing_percentage}% ({assessment.passing_score} pts)</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Proctoring</p>
                  <p className="font-medium text-gray-900">{assessment.is_proctored ? 'Enabled' : 'Disabled'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Copy-Paste</p>
                  <p className="font-medium text-gray-900">{assessment.allow_copy_paste ? 'Allowed' : 'Blocked'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pre-Approved Candidates Tab */}
        {activeTab === 'preapproved' && (
          <div className="space-y-6">
            {/* Add Candidate Form */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UserPlus size={20} className="text-purple-600" />
                Add Pre-Approved Candidate
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Add candidate emails who are authorized to take this assessment. Only these candidates can access the test using the access code.
              </p>
              
              <form onSubmit={handleAddApprovedCandidate} className="flex gap-3">
                <input
                  type="email"
                  value={newApprovedEmail}
                  onChange={(e) => setNewApprovedEmail(e.target.value)}
                  placeholder="Enter candidate email"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="add-candidate-email-input"
                />
                <button
                  type="submit"
                  disabled={addingEmail}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
                  data-testid="add-candidate-btn"
                >
                  {addingEmail ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Plus size={18} />
                  )}
                  Add
                </button>
              </form>
            </div>
            
            {/* Pre-Approved List */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  Pre-Approved Candidates ({candidates.filter(c => c.status === 'invited').length})
                </h3>
                <span className="text-sm text-gray-500">
                  Only these emails can access the assessment
                </span>
              </div>
              
              {candidates.filter(c => c.status === 'invited').length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {candidates.filter(c => c.status === 'invited').map(candidate => (
                    <div key={candidate.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <Mail className="text-purple-600" size={18} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{candidate.email}</p>
                          <p className="text-sm text-gray-500">
                            Added {candidate.invited_at ? new Date(candidate.invited_at).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            }) : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveCandidate(candidate.id, candidate.email)}
                        disabled={removingEmail === candidate.id}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Remove candidate"
                        data-testid={`remove-candidate-${candidate.id}`}
                      >
                        {removingEmail === candidate.id ? (
                          <div className="w-4 h-4 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="text-gray-400" size={28} />
                  </div>
                  <p className="text-gray-500 mb-2">No pre-approved candidates yet</p>
                  <p className="text-sm text-gray-400">Add candidate emails above to allow them to take this assessment</p>
                </div>
              )}
            </div>
            
            {/* Candidates who started or completed */}
            {candidates.filter(c => c.status !== 'invited').length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">
                    Active Candidates ({candidates.filter(c => c.status !== 'invited').length})
                  </h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {candidates.filter(c => c.status !== 'invited').map(candidate => (
                    <div key={candidate.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          candidate.status === 'completed' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {candidate.status === 'completed' ? (
                            <CheckCircle className="text-green-600" size={18} />
                          ) : (
                            <Clock className="text-blue-600" size={18} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{candidate.candidate_name || candidate.email}</p>
                          <p className="text-sm text-gray-500">{candidate.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(candidate.status)}
                        {candidate.percentage !== null && (
                          <span className="text-sm font-medium text-gray-900">
                            {candidate.percentage}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Candidates Tab */}
        {activeTab === 'candidates' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Candidate</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Score</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Result</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Tab Switches</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Completed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {candidates.length > 0 ? candidates.map(candidate => (
                    <tr key={candidate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{candidate.candidate_name || 'Not registered'}</p>
                          <p className="text-sm text-gray-500">{candidate.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getStatusBadge(candidate.status)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {candidate.score !== null ? (
                          <span className="font-semibold text-gray-900">
                            {candidate.score}/{assessment.total_points}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {candidate.passed !== null ? (
                          candidate.passed ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              Passed ({candidate.percentage}%)
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              Failed ({candidate.percentage}%)
                            </span>
                          )
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {candidate.tab_switches > 0 ? (
                          <span className="text-amber-600 font-medium">{candidate.tab_switches}</span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {candidate.completed_at ? formatDate(candidate.completed_at) : '-'}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <Users className="mx-auto text-gray-300 mb-3" size={40} />
                        <p className="text-gray-500">No candidates yet</p>
                        <button
                          onClick={() => setShowInviteModal(true)}
                          className="mt-3 text-purple-600 hover:underline text-sm"
                        >
                          Invite candidates
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Invite Candidates</h2>
              <p className="text-gray-500 text-sm mt-1">Send email invitations to candidates</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Addresses *
                </label>
                <textarea
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter emails separated by commas or new lines&#10;e.g., john@example.com, jane@example.com"
                  data-testid="invite-emails-input"
                />
                <p className="text-xs text-gray-400 mt-1">Separate multiple emails with commas or new lines</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Message (Optional)
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Add a personal note to the invitation..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviting}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50"
                data-testid="send-invites-btn"
              >
                {inviting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={18} />
                    Send Invitations
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentDetailPage;
